import React, { useState } from 'react';
import { Lock, Loader2 } from 'lucide-react';
import { track, EVENTS } from '../../lib/analytics';

/**
 * Unlock button: RM 10, mock payment.
 * - Click -> processing for 1.5s -> success -> onUnlock()
 * - Failure path available via the `?mock_fail=1` query for testing
 * - Shows inline error + retry on failure.
 */
const UnlockButton = ({ reportId, sticky = false, onUnlock }) => {
  const [status, setStatus] = useState('idle'); // idle | processing | error
  const [error, setError] = useState(null);

  const handleClick = async () => {
    track(EVENTS.UPGRADE_CLICK, { report_id: reportId });
    setError(null);
    setStatus('processing');
    try {
      // Simulate mock payment latency
      await new Promise((r) => setTimeout(r, 1500));

      // Query-string override to simulate failure
      const qs = new URLSearchParams(window.location.search);
      if (qs.get('mock_fail') === '1') {
        throw new Error('Payment could not be completed. Please try again.');
      }

      track(EVENTS.PAYMENT_SUCCESS, { report_id: reportId, amount: 10 });
      setStatus('idle');
      onUnlock?.();
    } catch (e) {
      track(EVENTS.PAYMENT_FAILURE, { report_id: reportId, message: e.message });
      setStatus('error');
      setError(e.message || 'Payment failed. Please try again.');
    }
  };

  const button = (
    <button
      type="button"
      onClick={handleClick}
      disabled={status === 'processing'}
      data-testid="unlock-report-btn"
      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-[#E85D35] hover:bg-[#C84B31] active:bg-[#B43F29] disabled:bg-[#F28B66] disabled:cursor-not-allowed transition shadow-md"
      style={{ minWidth: sticky ? '100%' : '220px' }}
    >
      {status === 'processing' ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" /> Processing…
        </>
      ) : (
        <>
          <Lock className="w-4 h-4" /> Unlock Full Intelligence · RM 10
        </>
      )}
    </button>
  );

  if (sticky) {
    // Mobile sticky band
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-[#FEFDF8]/95 backdrop-blur-md border-t border-[#E8DED3] p-4 sm:hidden"
        data-testid="unlock-sticky-bar"
      >
        {button}
        {status === 'error' && (
          <p className="text-xs text-red-600 mt-2 text-center" data-testid="unlock-error">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-[#FFF5EB] border border-[#E8DED3] p-6 text-center">
      <h4 className="text-xl font-bold text-[#2C2823] mb-2">
        Unlock the Full School Intelligence Report
      </h4>
      <p className="text-sm text-[#6B6560] mb-5 max-w-md mx-auto">
        Everything we know about your top 3 matches in one place — academic results, accreditations,
        awards and quality signals, co-curriculars — as available from verified sources
      </p>
      <div className="flex justify-center">{button}</div>
      <p className="text-xs text-[#9B8F88] mt-3">
        One-time payment · Valid 24 hours · No account needed
      </p>
      {status === 'error' && (
        <p className="text-sm text-red-600 mt-3" data-testid="unlock-error">
          {error}
        </p>
      )}
    </div>
  );
};

export default UnlockButton;
